/**
 * ArtsHome — /arts
 * Landing for Arts, Music & Cinema. Tiles for all 18 sections with
 * live counts, plus a horizontal "Featured" strip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Palette, Music, Film, Tv, Drama, Music2, Brush, BookOpen,
  Camera, Landmark, Sparkles, PartyPopper, CalendarDays, GraduationCap,
  Building2, Users, Search, Star, ArrowRight, PenSquare,
} from "lucide-react";
import { http } from "@/lib/api";

const SECTION_ICONS = {
  artists:       Palette,
  music:         Music,
  cinema:        Film,
  television:    Tv,
  theatre:       Drama,
  dance:         Music2,
  "fine-arts":   Brush,
  literature:    BookOpen,
  photography:   Camera,
  museums:       Landmark,
  heritage:      Sparkles,
  festivals:     PartyPopper,
  events:        CalendarDays,
  "art-schools":   GraduationCap,
  "music-schools": GraduationCap,
  "film-schools":  GraduationCap,
  production:    Building2,
  talent:        Users,
  articles:      PenSquare,
};

export default function ArtsHome() {
  const [meta, setMeta] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const m = await http.get("/arts/meta").then(r => r.data);
        setMeta(m);
      } catch { setMeta({ sections: [], counts: {} }); }
      try {
        const f = await http.get("/arts/entities?limit=8").then(r => r.data);
        setFeatured(f.items || []);
      } catch { setFeatured([]); }
    })();
  }, []);

  const search = (e) => {
    e.preventDefault();
    if (q.trim()) window.location.assign(`/arts/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="arts-home">
      {/* Hero */}
      <section className="mb-8">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>ARTS · MUSIC · CINEMA</div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
          A home for every creator and every curious mind.
        </h1>
        <p className="text-base md:text-lg opacity-80 max-w-3xl">
          Discover artists, musicians, filmmakers, dancers, writers,
          museums and festivals from every corner of the world — and
          share your own work with a global audience.
        </p>

        <form onSubmit={search} className="mt-5 flex gap-2 max-w-xl" data-testid="arts-search-form">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search artists, films, museums, festivals…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="arts-search-input"
            />
          </div>
          <button type="submit" className="wofy-btn-primary" data-testid="arts-search-submit">Search</button>
          <Link to="/arts/submit" className="wofy-btn-ghost" data-testid="arts-submit-cta">
            <PenSquare className="w-4 h-4" /> Add a profile
          </Link>
        </form>
      </section>

      {/* Sections grid */}
      <section className="mb-10">
        <h2 className="font-display text-2xl mb-4">Browse by section</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="arts-sections-grid">
          {(meta?.sections || []).map((s) => {
            const Icon = SECTION_ICONS[s.slug] || Palette;
            const count = meta?.counts?.[s.slug] || 0;
            return (
              <Link
                key={s.slug}
                to={`/arts/${s.slug}`}
                data-testid={`arts-section-tile-${s.slug}`}
                className="group rounded-xl border p-4 transition hover:bg-black/5"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="rounded-full p-2"
                       style={{ background: "var(--wofy-primary-soft, rgba(178,93,56,0.08))" }}>
                    <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
                  </div>
                  <span className="text-[10px] font-mono opacity-60">{count}</span>
                </div>
                <div className="font-medium text-sm group-hover:underline">{s.label}</div>
                <div className="text-[11px] opacity-70 mt-0.5 line-clamp-2">{s.tagline}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured strip */}
      {featured.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-4">Featured this week</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="arts-featured-strip">
            {featured.map((it) => (
              <Link
                key={it.slug}
                to={`/arts/${it.section}/${it.slug}`}
                data-testid={`arts-featured-${it.slug}`}
                className="group rounded-xl border p-4 hover:bg-black/5 transition"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {it.is_featured && (
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1 inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" style={{ color: "var(--wofy-primary)" }} /> Featured
                  </div>
                )}
                <div className="font-medium leading-tight group-hover:underline">{it.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mt-0.5">
                  {it.section}{it.category ? ` · ${it.category}` : ""}
                </div>
                {it.tagline && <p className="text-xs opacity-75 mt-1 line-clamp-2">{it.tagline}</p>}
                <div className="mt-2 text-xs inline-flex items-center gap-1"
                     style={{ color: "var(--wofy-primary)" }}>
                  Read more <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Contribute CTA */}
      <section className="wofy-card !p-6 text-center" data-testid="arts-contribute-cta">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>SHARE YOUR CRAFT</div>
        <h3 className="font-display text-2xl mb-2">Add your artist, gallery, school or festival</h3>
        <p className="text-sm opacity-80 mb-4 max-w-2xl mx-auto">
          A profile takes 3 minutes to create. Add photos, videos, awards,
          exhibitions and contact info — and let Social Pulse help you
          reach a global audience.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link to="/arts/submit" className="wofy-btn-primary inline-flex" data-testid="arts-contribute-link">
            <PenSquare className="w-4 h-4" /> Create a profile
          </Link>
          <Link to="/arts/awards" className="wofy-btn-ghost inline-flex" data-testid="arts-awards-link">
            Browse awards →
          </Link>
          <Link to="/arts/marketplace" className="wofy-btn-ghost inline-flex" data-testid="arts-marketplace-link">
            Shop artwork →
          </Link>
        </div>
      </section>
    </div>
  );
}
